import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import SuperAdminNav from '../components/SuperAdminNav';

const LIBRARY_CATEGORIES = [
  'Ball mastery',
  'Dribbling and 1v1 moves',
  'First touch and receiving',
  'Passing and wall work',
  'Shooting and finishing',
  'Juggling and aerial control',
  'Speed/agility/footwork',
  'Goalkeeping',
];

const AGE_RANGES = [
  { label: 'Under 10', value: 'Under 10' },
  { label: '10\u201313', value: '10-13' },
  { label: '14+', value: '14+' },
];

function getYouTubeThumbnail(url) {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?]+)/);
  if (match) return `https://img.youtube.com/vi/${match[1]}/mqdefault.jpg`;
  return null;
}

function getYouTubeEmbedUrl(url) {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?]+)/);
  if (match) return `https://www.youtube.com/embed/${match[1]}`;
  return null;
}

function truncateDescription(desc, wordCount = 18) {
  if (!desc) return '';
  const words = desc.split(/\s+/);
  if (words.length <= wordCount) return desc;
  return words.slice(0, wordCount).join(' ') + '...';
}

const CATEGORY_IMAGES = {
  'Ball mastery': { slug: 'ball-mastery', count: 2 },
  'Dribbling and 1v1 moves': { slug: 'dribbling-and-1v1-moves', count: 2 },
  'First touch and receiving': { slug: 'first-touch-and-receiving', count: 2 },
  'Passing and wall work': { slug: 'passing-and-wall-work', count: 2 },
  'Shooting and finishing': { slug: 'shooting-and-finishing', count: 2 },
  'Juggling and aerial control': { slug: 'juggling-and-aerial-control', count: 2 },
  'Speed/agility/footwork': { slug: 'speed-agility-and-footwork', count: 5 },
  'Goalkeeping': { slug: 'goalkeeping', count: 2 },
};

function getCategoryThumbnail(category, variant) {
  const info = CATEGORY_IMAGES[category];
  if (!info) return null;
  const idx = variant % info.count;
  const suffix = idx === 0 ? '' : String(idx + 1);
  return `/library/${info.slug}${suffix}.png`;
}

function parseLibraryCSV(text) {
  const lines = text.trim().split('\n').filter(l => l.trim());
  if (lines.length === 0) return [];
  let start = 0;
  const firstLine = lines[0].toLowerCase();
  if (firstLine.includes('title') && (firstLine.includes('url') || firstLine.includes('description'))) start = 1;

  return lines.slice(start).map(line => {
    // Handle CSV with commas inside quotes
    const cells = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        inQuotes = !inQuotes;
      } else if (ch === ',' && !inQuotes) {
        cells.push(current.trim());
        current = '';
      } else {
        current += ch;
      }
    }
    cells.push(current.trim());

    const premiumVal = (cells[6] || '').trim().toLowerCase();
    return {
      title: cells[0] || '',
      description: cells[1] || '',
      youtube_url: cells[2] || '',
      age_ranges: (cells[3] || '').split('|').map(s => s.trim()).filter(Boolean).map(a => a === '14' ? '14+' : a),
      categories: (cells[4] || '').split('|').map(s => s.trim()).filter(Boolean),
      live_date: (cells[5] || '').trim(),
      is_premium: ['true', '1', 'premium', 'yes'].includes(premiumVal),
    };
  });
}

export default function SuperLibrary() {
  const { apiFetch, token } = useAuth();
  const navigate = useNavigate();
  const [drills, setDrills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', youtube_url: '', categories: [], age_ranges: [], primary_category: '', live_date: '', is_premium: false });
  const [saving, setSaving] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [csvText, setCsvText] = useState('');
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [videoDrill, setVideoDrill] = useState(null);
  const [backfilling, setBackfilling] = useState(false);

  // Filters
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [selectedAgeRanges, setSelectedAgeRanges] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);

  const loadDrills = useCallback(async () => {
    try {
      const data = await apiFetch('/api/super/library');
      setDrills(data.drills);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => { loadDrills(); }, [loadDrills]);

  const openAdd = () => {
    setEditing(null);
    setForm({ title: '', description: '', youtube_url: '', categories: [], age_ranges: [], primary_category: '', live_date: '', is_premium: false });
    setShowModal(true);
  };

  const formatLiveDateForForm = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    return `${String(dt.getUTCMonth() + 1).padStart(2, '0')}/${String(dt.getUTCDate()).padStart(2, '0')}/${dt.getUTCFullYear()}`;
  };

  const openEdit = (drill) => {
    setEditing(drill);
    setForm({
      title: drill.title || '',
      description: drill.description || '',
      youtube_url: drill.youtube_url || '',
      categories: drill.categories || [],
      age_ranges: drill.age_ranges || [],
      primary_category: drill.primary_category || '',
      live_date: formatLiveDateForForm(drill.live_date),
      is_premium: drill.is_premium || false,
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    // Auto-set primary_category: if 1 category, use it; if video URL present, clear it
    const saveForm = { ...form };
    if (saveForm.categories.length === 1) {
      saveForm.primary_category = saveForm.categories[0];
    } else if (saveForm.categories.length === 0) {
      saveForm.primary_category = '';
    }
    // Convert MM/DD/YYYY to ISO date for the server
    if (saveForm.live_date && saveForm.live_date.trim()) {
      const match = saveForm.live_date.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (match) {
        const d = new Date(parseInt(match[3]), parseInt(match[1]) - 1, parseInt(match[2]));
        if (!isNaN(d.getTime())) {
          saveForm.live_date = d.toISOString().split('T')[0];
        } else {
          alert('Invalid Live Date. Use MM/DD/YYYY format.');
          setSaving(false);
          return;
        }
      } else {
        alert('Invalid Live Date. Use MM/DD/YYYY format.');
        setSaving(false);
        return;
      }
    } else {
      saveForm.live_date = null;
    }
    try {
      if (editing) {
        await apiFetch(`/api/super/library/${editing.id}`, {
          method: 'PUT',
          body: JSON.stringify(saveForm),
        });
      } else {
        await apiFetch('/api/super/library', {
          method: 'POST',
          body: JSON.stringify(saveForm),
        });
      }
      setShowModal(false);
      loadDrills();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this drill from the library?')) return;
    try {
      await apiFetch(`/api/super/library/${id}`, { method: 'DELETE' });
      loadDrills();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleUseAsDrill = (drill) => {
    navigate('/super/lessons', { state: { prefill: { title: drill.title, description: drill.description, youtube_url: drill.youtube_url } } });
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const toggleFilterAgeRange = (val) => {
    setSelectedAgeRanges(prev => prev.includes(val) ? prev.filter(a => a !== val) : [...prev, val]);
  };

  const toggleFilterCategory = (cat) => {
    setSelectedCategories(prev => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]);
  };

  const filteredDrills = drills.filter(drill => {
    if (search) {
      const q = search.toLowerCase();
      if (!(drill.title || '').toLowerCase().includes(q) && !(drill.description || '').toLowerCase().includes(q)) return false;
    }
    if (selectedAgeRanges.length > 0) {
      if (!drill.age_ranges || !selectedAgeRanges.some(ar => drill.age_ranges.includes(ar))) return false;
    }
    if (selectedCategories.length > 0) {
      if (!drill.categories || !selectedCategories.some(c => drill.categories.includes(c))) return false;
    }
    return true;
  });

  const toggleCategory = (cat) => {
    setForm(f => {
      const newCats = f.categories.includes(cat) ? f.categories.filter(c => c !== cat) : [...f.categories, cat];
      let pc = f.primary_category;
      if (newCats.length === 1) pc = newCats[0];
      else if (newCats.length === 0) pc = '';
      else if (pc && !newCats.includes(pc)) pc = newCats[0];
      return { ...f, categories: newCats, primary_category: pc };
    });
  };

  const toggleAgeRange = (val) => {
    setForm(f => ({
      ...f,
      age_ranges: f.age_ranges.includes(val) ? f.age_ranges.filter(a => a !== val) : [...f.age_ranges, val],
    }));
  };

  const handleCSVFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setCsvText(ev.target.result);
    };
    reader.readAsText(file);
  };

  const handlePreviewImport = () => {
    const rows = parseLibraryCSV(csvText);
    setImportPreview(rows);
  };

  const handleCommitImport = async () => {
    if (!importPreview || importPreview.length === 0) return;
    setImporting(true);
    try {
      const data = await apiFetch('/api/super/library/import', {
        method: 'POST',
        body: JSON.stringify({ rows: importPreview }),
      });
      alert(`Imported ${data.imported} drills.${data.skipped ? ` ${data.skipped} skipped (duplicate YouTube video).` : ''}${data.errors.length > 0 ? ' Errors: ' + data.errors.join('; ') : ''}`);
      setShowImport(false);
      setCsvText('');
      setImportPreview(null);
      loadDrills();
    } catch (err) {
      alert(err.message);
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/super/library/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'drill-library.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.message);
    }
  };

  const handleBackfillDurations = async () => {
    if (!window.confirm('Fetch YouTube video durations for all library drills missing them?')) return;
    setBackfilling(true);
    try {
      const data = await apiFetch('/api/super/library/backfill-durations', { method: 'POST' });
      alert(`Updated ${data.updated} of ${data.total} drills`);
      loadDrills();
    } catch (err) {
      alert('Backfill failed: ' + (err.message || 'Unknown error'));
    } finally {
      setBackfilling(false);
    }
  };

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  return (
    <div className="admin-layout">
      <SuperAdminNav activeItem="library" />
      <div className="admin-page">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h1 className="page-title">Drill Library</h1>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-sm btn-outline" onClick={handleBackfillDurations} disabled={backfilling}>
              {backfilling ? 'Fetching...' : 'Backfill Durations'}
            </button>
            <button className="btn btn-sm btn-outline" onClick={handleExport}>Export CSV</button>
            <button className="btn btn-sm btn-outline" onClick={() => { setShowImport(true); setImportPreview(null); setCsvText(''); }}>Import CSV</button>
            <button className="btn btn-sm btn-orange" onClick={openAdd}>+ Add Drill</button>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="library-filters">
          <div className="library-filter-row">
            <input
              className="form-input"
              placeholder="Search drills..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              style={{ maxWidth: 260 }}
            />
            <div style={{ display: 'flex', gap: 6 }}>
              {AGE_RANGES.map(ar => (
                <button
                  key={ar.value}
                  className={`btn btn-sm ${selectedAgeRanges.includes(ar.value) ? 'btn-orange' : 'btn-outline'}`}
                  onClick={() => toggleFilterAgeRange(ar.value)}
                >
                  {ar.label}
                </button>
              ))}
            </div>
            <div style={{ position: 'relative' }}>
              <button
                className={`btn btn-sm ${selectedCategories.length > 0 ? 'btn-orange' : 'btn-outline'}`}
                onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
              >
                Category{selectedCategories.length > 0 ? ` (${selectedCategories.length})` : ''}
              </button>
              {showCategoryDropdown && (
                <div className="category-dropdown">
                  {LIBRARY_CATEGORIES.map(cat => (
                    <label key={cat} className="category-dropdown-item">
                      <input type="checkbox" checked={selectedCategories.includes(cat)} onChange={() => toggleFilterCategory(cat)} />
                      {cat}
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Drill List */}
        <div className="library-grid">
          {(() => { const catCounters = {}; return filteredDrills.map(drill => {
            const pc = drill.primary_category || (drill.categories && drill.categories[0]);
            const variant = pc ? (catCounters[pc] = (catCounters[pc] || 0) + 1, catCounters[pc] - 1) : 0;
            const catThumb = pc ? getCategoryThumbnail(pc, variant) : null;
            return (
            <div key={drill.id} className="library-card">
              {drill.youtube_url && getYouTubeThumbnail(drill.youtube_url) ? (
                <img
                  src={getYouTubeThumbnail(drill.youtube_url)}
                  alt={drill.title}
                  className="library-thumbnail"
                  onClick={() => setVideoDrill(drill)}
                  style={{ cursor: 'pointer' }}
                />
              ) : catThumb ? (
                <img
                  src={catThumb}
                  alt={pc}
                  className="library-thumbnail"
                />
              ) : (
                <div className="library-thumbnail library-thumbnail-placeholder">
                  No Video
                </div>
              )}
              <div className="library-card-body">
                <h3 className="library-card-title">{drill.title}</h3>
                <p className="library-card-desc">{truncateDescription(drill.description)}</p>
                <div className="library-tags">
                  {drill.live_date && new Date(drill.live_date + 'T05:00:00-05:00') > new Date() && (
                    <span className="library-tag" style={{ background: '#3a1a1a', color: '#f59e0b' }}>Goes live {formatLiveDateForForm(drill.live_date)}</span>
                  )}
                  {drill.target_time_minutes && (
                    <span className="library-tag" style={{ background: '#1a3a1a', color: '#4ade80' }}>{drill.target_time_minutes} min</span>
                  )}
                  {(drill.age_ranges || []).map(ar => (
                    <span key={ar} className="library-tag age-tag">{ar}</span>
                  ))}
                  {(drill.categories || []).map(cat => (
                    <span key={cat} className="library-tag category-tag">{cat}</span>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                  <button className="btn btn-sm btn-blue" onClick={() => handleUseAsDrill(drill)}>Use as Drill</button>
                  <button className="btn btn-sm btn-outline" onClick={() => openEdit(drill)}>Edit</button>
                  <button className="btn btn-sm btn-danger" onClick={() => handleDelete(drill.id)}>Delete</button>
                </div>
              </div>
            </div>
            );
          }); })()}
        </div>

        {filteredDrills.length === 0 && (
          <p style={{ color: 'var(--text-muted)', textAlign: 'center', marginTop: 32 }}>{drills.length === 0 ? 'No drills in library yet. Add one above or import via CSV.' : 'No drills found matching your filters.'}</p>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 540 }}>
            <h2>{editing ? 'Edit' : 'Add'} Library Drill</h2>
            <form onSubmit={handleSave}>
              <div className="form-group">
                <label className="form-label">Title *</label>
                <input className="form-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-textarea" rows={4} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">YouTube URL</label>
                <input className="form-input" value={form.youtube_url} onChange={e => setForm({ ...form, youtube_url: e.target.value })} placeholder="https://youtube.com/watch?v=..." />
              </div>
              <div className="form-group">
                <label className="form-label">Age Ranges</label>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  {AGE_RANGES.map(ar => (
                    <label key={ar.value} className="form-checkbox">
                      <input type="checkbox" checked={form.age_ranges.includes(ar.value)} onChange={() => toggleAgeRange(ar.value)} />
                      {ar.label}
                    </label>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Categories</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {LIBRARY_CATEGORIES.map(cat => (
                    <label key={cat} className="form-checkbox">
                      <input type="checkbox" checked={form.categories.includes(cat)} onChange={() => toggleCategory(cat)} />
                      {cat}
                    </label>
                  ))}
                </div>
              </div>
              {!form.youtube_url && form.categories.length >= 2 && (
                <div className="form-group">
                  <label className="form-label">Primary Category (for thumbnail image)</label>
                  <select
                    className="form-input"
                    value={form.primary_category}
                    onChange={e => setForm({ ...form, primary_category: e.target.value })}
                  >
                    {form.categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Live Date (MM/DD/YYYY)</label>
                <input className="form-input" value={form.live_date} onChange={e => setForm({ ...form, live_date: e.target.value })} placeholder="MM/DD/YYYY (blank = immediate)" style={{ maxWidth: 200 }} />
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Goes live at 5:00am ET on this date. Leave blank for immediate.</span>
              </div>
              <div className="form-group" style={{ marginTop: 8 }}>
                <label className="form-checkbox">
                  <input type="checkbox" checked={form.is_premium} onChange={e => setForm({ ...form, is_premium: e.target.checked })} />
                  <span>Premium Drill</span>
                </label>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Premium drills are only accessible to paid coaches.</span>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                <button type="submit" className="btn btn-orange" disabled={saving}>{saving ? 'Saving...' : (editing ? 'Update' : 'Add Drill')}</button>
                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Import CSV Modal */}
      {showImport && (
        <div className="modal-overlay" onClick={() => setShowImport(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
            <h2>Import Drills from CSV</h2>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: 12 }}>
              CSV format: Title, Description, YouTube URL, Age Ranges (pipe-separated), Categories (pipe-separated), Live Date (MM/DD/YYYY, optional), Premium (true/false, optional)
            </p>
            <div className="form-group">
              <label className="form-label">Upload CSV file</label>
              <input type="file" accept=".csv,.txt" onChange={handleCSVFile} style={{ color: 'var(--white)' }} />
            </div>
            <div className="form-group">
              <label className="form-label">Or paste CSV text</label>
              <textarea className="form-textarea" rows={6} value={csvText} onChange={e => setCsvText(e.target.value)} placeholder="Title, Description, YouTube URL, Age Ranges, Categories, Live Date" />
            </div>
            {!importPreview && (
              <button className="btn btn-blue" onClick={handlePreviewImport} disabled={!csvText.trim()}>Preview</button>
            )}
            {importPreview && (
              <div style={{ marginTop: 12 }}>
                <p style={{ color: 'var(--white)', marginBottom: 8 }}><strong>{importPreview.length}</strong> drills found:</p>
                <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid var(--card-border)', borderRadius: 8, padding: 8 }}>
                  {importPreview.map((row, i) => (
                    <div key={i} style={{ fontSize: '0.8rem', color: 'var(--text-muted)', padding: '4px 0', borderBottom: '1px solid var(--card-border)' }}>
                      <strong style={{ color: 'var(--white)' }}>{row.title}</strong> — {row.age_ranges.join(', ')} — {row.categories.join(', ')}{row.live_date ? ` — Live: ${row.live_date}` : ''}
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button className="btn btn-orange" onClick={handleCommitImport} disabled={importing}>{importing ? 'Importing...' : 'Import All'}</button>
                  <button className="btn btn-outline" onClick={() => setShowImport(false)}>Cancel</button>
                </div>
              </div>
            )}
            {!importPreview && (
              <button className="btn btn-outline" onClick={() => setShowImport(false)} style={{ marginLeft: 8 }}>Cancel</button>
            )}
          </div>
        </div>
      )}

      {/* Video Playback Modal */}
      {videoDrill && (
        <div className="modal-overlay" onClick={() => setVideoDrill(null)}>
          <div className="video-modal" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0, color: 'var(--white)' }}>{videoDrill.title}</h3>
              <button className="btn btn-sm btn-outline" onClick={() => setVideoDrill(null)}>Close</button>
            </div>
            <div className="video-container">
              <iframe
                src={getYouTubeEmbedUrl(videoDrill.youtube_url)}
                title={videoDrill.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ width: '100%', height: '100%', border: 'none' }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
